// VMT Reduction Component Types

export interface VmtReductionComponents {
  transitOrientedDevelopment: number; // 0-2% (annual housing growth rate; same as Compact Development detail slider)
  activeTransportation: number; // 0-10%
  transitImprovements: number; // 0-10% (VMT reduction %)
}

// Energy Efficiency Component Types (multi-policy, segment-based)
export interface EnergyEfficiencyComponents {
  /** % reduction in therms/kWh intensity for new residential stock by 2045 */
  residentialReachCodePercent: number; // 0-40
  /** % reduction in therms/kWh intensity for new commercial stock by 2045 */
  commercialReachCodePercent: number; // 0-40
  /** % of existing stock reached by weatherization/retrofit programs by 2045; converted to effective reduction */
  weatherizationPercent: number; // 0-50
  /** % reduction in total building electricity (kWh) by 2045; cap 5% */
  demandResponsePercent: number; // 0-5
  /** Residual / other (computed when in target mode; 0 in policy mode) */
  otherEfficiencyPercent: number; // 0-40, read-only in expanded view
}

/** Default allocation when distributing a target to components (target mode). */
export const DEFAULT_ENERGY_EFFICIENCY_DISTRIBUTION = {
  weatherizationPercent: 0.5,       // 50% of target
  residentialReachCodePercent: 0.2, // 20%
  commercialReachCodePercent: 0.15, // 15%
  demandResponsePercent: 0.05,      // 5% (capped at 5 absolute)
};

export function getDefaultEnergyEfficiencyComponents(): EnergyEfficiencyComponents {
  return {
    residentialReachCodePercent: 0,
    commercialReachCodePercent: 0,
    weatherizationPercent: 0,
    demandResponsePercent: 0,
    otherEfficiencyPercent: 0,
  };
}

/** True if any efficiency component has non-zero reduction. */
export function hasAnyEfficiencyComponent(c: EnergyEfficiencyComponents): boolean {
  return (
    (c.residentialReachCodePercent ?? 0) > 0 ||
    (c.commercialReachCodePercent ?? 0) > 0 ||
    (c.weatherizationPercent ?? 0) > 0 ||
    (c.demandResponsePercent ?? 0) > 0 ||
    (c.otherEfficiencyPercent ?? 0) > 0
  );
}

/** Distribute a target % to components (for collapsed/target mode). Residual goes to other. */
export function distributeEnergyEfficiencyTarget(
  targetPercent: number,
  currentComponents: EnergyEfficiencyComponents
): EnergyEfficiencyComponents {
  const d = DEFAULT_ENERGY_EFFICIENCY_DISTRIBUTION;
  const weatherization = Math.min(50, targetPercent * d.weatherizationPercent);
  const resReach = Math.min(40, targetPercent * d.residentialReachCodePercent);
  const comReach = Math.min(40, targetPercent * d.commercialReachCodePercent);
  const dr = Math.min(5, targetPercent * d.demandResponsePercent);
  const other = Math.max(0, targetPercent - weatherization - resReach - comReach - dr);
  return {
    residentialReachCodePercent: resReach,
    commercialReachCodePercent: comReach,
    weatherizationPercent: weatherization,
    demandResponsePercent: dr,
    otherEfficiencyPercent: other,
  };
}

export interface ParkingPricingAssumptions {
  priceIncreasePercent: number; // 10-300% (main lever)
  vmtAffectedPercent: number; // Percentage of total VMT that can be affected (default 25%)
  startingPricePerHour: number; // Starting parking price per hour (default $0)
  commuteByCarPercent: number; // Percentage who commute by car alone (fetched from database)
}

export interface InterventionAreas {
  // Transportation (Light Blue)
  urbanInfill: number; // 0-100% by 2045
  vmtReduction: number; // 0-20% by 2045
  vehicleElectrification: number; // 0-100% by 2045
  // Buildings (Yellow)
  energyEfficiency: number; // 0-100% by 2045
  buildingsElectrification: number; // 0-100% by 2045
  gridDecarbonization: number; // year electricity reaches zero emissions (2025-2045)
  // Other (Orange)
  organicsDiversion: number; // 0-100% by 2045
}

export interface VmtComponentMetadata {
  key: keyof VmtReductionComponents;
  label: string;
  description: string;
  maxPercent: number;
  color: string;
  literatureSource: string;
}

// Component metadata with colors (shades of green to match existing VMT green #10b981)
export const VMT_COMPONENT_METADATA: VmtComponentMetadata[] = [
  {
    key: 'transitOrientedDevelopment',
    label: 'Compact Development',
    description: 'Percent growth per year (0–2%): annual policy housing growth rate; shifts growth into lower-VMT cities; worksheet models out-of-boundary and net statewide savings',
    maxPercent: 2,
    color: '#059669', // Green-600
    literatureSource: 'Compact development elasticity; density effect (-0.22)',
  },
  {
    key: 'activeTransportation',
    label: 'Active Transportation Infrastructure',
    description: 'Build protected bike lanes, pedestrian infrastructure, and complete streets',
    maxPercent: 10,
    color: '#10b981', // Green-500 (main VMT color)
    literatureSource: 'CARB Technical Guidance on Bicycle Facilities',
  },
  {
    key: 'transitImprovements',
    label: 'Transit Service Improvements',
    description: 'Expand transit frequency, coverage, and provide fare subsidies',
    maxPercent: 10,
    color: '#6ee7b7', // Green-300
    literatureSource: 'Transit proximity: 25-50% VMT reduction',
  },
];

// Default distribution ratios (used when scaling from total slider)
export const DEFAULT_VMT_DISTRIBUTION = {
  transitOrientedDevelopment: 0.5, // 50% of total
  activeTransportation: 0.2, // 20% of total
  transitImprovements: 0.3, // 30% of total
};

// Helper function to get default (zero) components
export function getDefaultVmtComponents(): VmtReductionComponents {
  return {
    transitOrientedDevelopment: 0,
    activeTransportation: 0,
    transitImprovements: 0,
  };
}

// Helper function to get default (zero) intervention areas
export function getDefaultInterventionAreas(): InterventionAreas {
  return {
    urbanInfill: 0,
    vmtReduction: 0,
    vehicleElectrification: 0,
    energyEfficiency: 0,
    buildingsElectrification: 0,
    // Default clean electricity year per SB 100.
    gridDecarbonization: 2045,
    organicsDiversion: 50,
  };
}

// Calculate total VMT reduction from components
export function calculateTotalVmtReduction(components: VmtReductionComponents): number {
  return (
    components.transitOrientedDevelopment +
    components.activeTransportation +
    components.transitImprovements
  );
}

// Distribute total VMT reduction across components proportionally
export function distributeVmtReduction(
  totalPercent: number,
  currentComponents: VmtReductionComponents
): VmtReductionComponents {
  const currentTotal = calculateTotalVmtReduction(currentComponents);

  if (currentTotal === 0) {
    // Use default distribution based on literature-supported ratios
    return {
      transitOrientedDevelopment: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitOrientedDevelopment, 2),
      activeTransportation: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.activeTransportation, 10),
      transitImprovements: Math.min(totalPercent * DEFAULT_VMT_DISTRIBUTION.transitImprovements, 10),
    };
  }

  // Proportional scaling
  const scaleFactor = totalPercent / currentTotal;
  return {
    transitOrientedDevelopment: Math.min(currentComponents.transitOrientedDevelopment * scaleFactor, 2),
    activeTransportation: Math.min(currentComponents.activeTransportation * scaleFactor, 10),
    transitImprovements: Math.min(currentComponents.transitImprovements * scaleFactor, 10),
  };
}

// Validate VMT components
export function validateVmtComponents(
  components: VmtReductionComponents
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (components.transitOrientedDevelopment > 2) {
    errors.push('Compact Development exceeds max (2%)');
  }
  if (components.activeTransportation > 10) {
    errors.push('Active transport exceeds max (10%)');
  }
  if (components.transitImprovements > 10) {
    errors.push('Transit improvements exceeds max (10%)');
  }

  const total = calculateTotalVmtReduction(components);
  if (total > 50) {
    errors.push('Total VMT reduction exceeds 50%');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Micromobility assumptions used by the Policy Impact Tool
export interface MicromobilityAssumptions {
  yearImplemented: number; // default 2025
  tripsPerResident: number; // e.g., 3 trips per resident per year
  percentBike: number; // percent of program trips by e-bike (0-100)
  percentScooter: number; // percent of program trips by e-scooter (0-100)
  milesPerBikeTrip: number; // average miles per e-bike trip
  milesPerScooterTrip: number; // average miles per e-scooter trip
  percentBikeTripsDisplaceCars: number; // 0-100
  percentScooterTripsDisplaceCars: number; // 0-100
  kwhPerMileBike: number; // EV bike electricity consumption (kWh/mile)
  kwhPerMileScooter: number; // EV scooter electricity consumption (kWh/mile)
  scalePercent: number; // user scale control (0-200%)
}
